import TreeIcon from "@mui/icons-material/AccountTree";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DecisionTreeResult } from "../../api/case/decisionTrees";
import { useCaseApi } from "../../api/case/parse/ParseCaseClient";
import { useCase } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { Stage } from "../../shared/CaseConfiguration";
import { percentageNoDecimals } from "../../shared/percentage";
import { DecisionTreeOfParty } from "../../shared/tree";
import { EmptyStateFrame, EmptyStateHeader } from "../general/EmptyState";
import { decisionTreesDot } from "./decisionTreeDot";
import { Graph } from "./Graph";
import { graphColors } from "./graphColors";

const COUNT = new Intl.NumberFormat("en-GB");

// why there is no tree to show: the server's reason, or the drawing failed
type Refusal =
  | Exclude<DecisionTreeResult, { state: "done" | "failed" }>
  | { state: "failed"; reason: "timeout" | "memory" | "error" | "drawing" };

type View =
  | { phase: "idle" }
  // progress: the share of the work the server has done, 0 to 1
  | { phase: "generating"; progress: number }
  | { phase: "drawing" | "drawn"; trees: DecisionTreeOfParty[] }
  | { phase: "refused"; refusal: Refusal };

/**
 * Dev tool: the decision trees of all parties of the case, for the users the
 * `decisionTree` feature toggle is on for.
 *
 * The trees are generated by the server when the user asks for them and live
 * in this component only: they are far too large to be part of the case, so
 * leaving the page drops them, and so does any change of the case, which they
 * would no longer describe.
 */
export const DecisionTree = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.tree",
  });
  const { t: tCase } = useTranslation(undefined, { keyPrefix: "case" });
  const api = useCaseApi();
  const caseContext = useCase();
  const c = caseContext.getCase();
  const parties = caseContext.getPartyDescriptors();
  const { theme } = useTheme();
  const { currency } = useCurrency();

  const version = `${c.id}:${c.updatedAt}`;
  const [shown, setShown] = useState<{ version: string; view: View }>();
  const view: View =
    shown?.version === version ? shown.view : { phase: "idle" };

  const asking = useRef<AbortController | undefined>(undefined);
  // nobody looks at the answer once the page is left or the case has changed
  useEffect(() => () => asking.current?.abort(), [version]);

  const generate = () => {
    asking.current?.abort();
    const controller = new AbortController();
    asking.current = controller;
    const show = (view: View) => {
      if (!controller.signal.aborted) setShown({ version, view });
    };

    show({ phase: "generating", progress: 0 });
    api
      .generateDecisionTrees(
        c.id,
        (progress) => show({ phase: "generating", progress }),
        controller.signal,
      )
      .then((result) =>
        show(
          result.state === "done"
            ? { phase: "drawing", trees: result.trees }
            : { phase: "refused", refusal: result },
        ),
      )
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.warn("Could not generate the decision tree", error);
        show({
          phase: "refused",
          refusal: { state: "failed", reason: "error" },
        });
      });
  };

  // drawn again when the look of the app changes under it
  const trees =
    view.phase === "drawing" || view.phase === "drawn" ? view.trees : undefined;
  const dot = useMemo(
    () =>
      trees &&
      decisionTreesDot(trees, {
        colors: graphColors(theme),
        money: currency.format,
        stageName: (stage) => tCase(`stages.${Stage[stage]}`),
        partyName: (partyId) => parties[partyId]?.name ?? partyId,
        isClient: (partyId) => parties[partyId]?.isClient === true,
        assetsOf: (partyId) => parties[partyId]?.assetLimit,
      }),
    [trees, theme, currency, tCase, parties],
  );

  if (trees !== undefined && dot !== undefined) {
    return (
      <Stack spacing={1.5}>
        {view.phase === "drawing" && (
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("drawing")}
            </Typography>
          </Stack>
        )}
        <Box sx={{ overflow: "auto" }}>
          <Graph
            dot={dot}
            onDrawn={() =>
              setShown({ version, view: { phase: "drawn", trees } })
            }
            onFailed={(message) => {
              console.warn("Could not draw the decision tree", message);
              setShown({
                version,
                view: {
                  phase: "refused",
                  refusal: { state: "failed", reason: "drawing" },
                },
              });
            }}
          />
        </Box>
      </Stack>
    );
  }

  const reason = (refusal: Refusal): string => {
    switch (refusal.state) {
      case "tooLarge":
        return t("tooLarge", {
          nodes: COUNT.format(refusal.nodes),
          maxNodes: COUNT.format(refusal.maxNodes),
        });
      case "busy":
        return t("busy");
      case "failed":
        return t(`failed.${refusal.reason}`);
    }
  };
  // the same case gives the same trees, so only what may differ next time
  const worthAnotherTry =
    view.phase === "refused" && view.refusal.state !== "tooLarge";

  return (
    <EmptyStateFrame minHeight="50vh">
      <EmptyStateHeader
        icon={<TreeIcon />}
        title={t("title")}
        intro={view.phase === "refused" ? reason(view.refusal) : t("intro")}
      />

      {view.phase === "generating" && (
        <Stack sx={{ width: "100%", alignItems: "center", gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={view.progress * 100}
            aria-label={t("generating")}
            sx={{ width: "100%" }}
          />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("generated", {
              share: percentageNoDecimals.format(view.progress),
            })}
          </Typography>
        </Stack>
      )}

      {(view.phase === "idle" || worthAnotherTry) && (
        <Button variant="contained" onClick={generate}>
          {t(view.phase === "idle" ? "generate" : "retry")}
        </Button>
      )}
    </EmptyStateFrame>
  );
};
